import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Users, CheckCircle, Clock, Settings, Save, KeyRound, List, Pencil, Trash2, X, Check } from 'lucide-react';

export default function AdminReferralsPage() {
  const { t } = useLanguage();
  const [referrals, setReferrals] = useState([]);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('list');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [refRes, statsRes, settingsRes, codesRes] = await Promise.all([
        api.getAdminReferrals(),
        api.getAdminReferralStats(),
        api.getAdminReferralSettings(),
        api.getAdminReferralCodes(),
      ]);
      setReferrals(refRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setCodes(codesRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.updateAdminReferralSettings({
        referrer_free_transfers: settings.referrer_free_transfers,
        referee_free_transfers: settings.referee_free_transfers,
        min_transfer_amount: settings.min_transfer_amount,
        tier_enabled: settings.tier_enabled,
        tiers: settings.tiers,
      });
      toast.success(t('admin_ref_settings_saved'));
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (code) => {
    setEditingId(code.id);
    setEditValue(code.code);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (codeId) => {
    try {
      await api.updateAdminReferralCode(codeId, { code: editValue });
      toast.success(t('admin_ref_code_updated'));
      setEditingId(null);
      setEditValue('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin_ref_code_update_failed'));
    }
  };

  const handleDeleteCode = async (codeId, codeName) => {
    if (!window.confirm(t('admin_ref_delete_confirm').replace('{code}', codeName))) return;
    try {
      await api.deleteAdminReferralCode(codeId);
      toast.success(t('admin_ref_code_deleted'));
      fetchData();
    } catch (e) {
      toast.error(t('admin_ref_delete_failed'));
    }
  };

  const updateTier = (idx, field, value) => {
    const newTiers = [...settings.tiers];
    newTiers[idx] = { ...newTiers[idx], [field]: field.includes('percent') || field.includes('referrals') ? Number(value) : value };
    setSettings({ ...settings, tiers: newTiers });
  };

  const handleStatClick = (type) => {
    setTab('list');
    setFilter(type);
  };

  const filteredReferrals = referrals.filter(ref => {
    if (filter === 'all') return true;
    if (filter === 'completed') return ref.status === 'completed';
    if (filter === 'pending') return ref.status === 'registered' || ref.status === 'pending';
    return true;
  });

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div data-testid="admin-referrals-page">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-primary">{t('admin_ref_title')}</h1>
        </div>

        {/* Stats - clickable */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => handleStatClick('all')}
            className={`bg-card border rounded-xl p-4 text-center transition-all hover:shadow-md hover:border-accent/40 ${tab === 'list' && filter === 'all' ? 'ring-2 ring-accent/50' : ''}`}
            data-testid="stat-total"
          >
            <Users className="h-5 w-5 mx-auto mb-1 text-accent" />
            <div className="text-2xl font-bold font-mono">{stats?.total_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">{t('admin_ref_total')}</div>
          </button>
          <button
            onClick={() => handleStatClick('completed')}
            className={`bg-card border rounded-xl p-4 text-center transition-all hover:shadow-md hover:border-emerald-400/40 ${tab === 'list' && filter === 'completed' ? 'ring-2 ring-emerald-500/50' : ''}`}
            data-testid="stat-completed"
          >
            <CheckCircle className="h-5 w-5 mx-auto mb-1 text-emerald-500" />
            <div className="text-2xl font-bold font-mono text-emerald-600">{stats?.completed_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">{t('admin_ref_completed')}</div>
          </button>
          <button
            onClick={() => handleStatClick('pending')}
            className={`bg-card border rounded-xl p-4 text-center transition-all hover:shadow-md hover:border-amber-400/40 ${tab === 'list' && filter === 'pending' ? 'ring-2 ring-amber-500/50' : ''}`}
            data-testid="stat-pending"
          >
            <Clock className="h-5 w-5 mx-auto mb-1 text-amber-500" />
            <div className="text-2xl font-bold font-mono text-amber-600">{stats?.pending_referrals || 0}</div>
            <div className="text-xs text-muted-foreground">{t('admin_ref_pending')}</div>
          </button>
          <button
            onClick={() => setTab('codes')}
            className={`bg-card border rounded-xl p-4 text-center transition-all hover:shadow-md hover:border-violet-400/40 ${tab === 'codes' ? 'ring-2 ring-violet-500/50' : ''}`}
            data-testid="stat-codes"
          >
            <KeyRound className="h-5 w-5 mx-auto mb-1 text-violet-500" />
            <div className="text-2xl font-bold font-mono text-violet-600">{stats?.total_codes || 0}</div>
            <div className="text-xs text-muted-foreground">{t('admin_ref_codes')}</div>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={tab === 'list' ? 'default' : 'outline'} size="sm" onClick={() => { setTab('list'); setFilter('all'); }} data-testid="tab-list">
            <List className="h-4 w-4 mr-1" /> {t('admin_ref_tab_list')}
          </Button>
          <Button variant={tab === 'codes' ? 'default' : 'outline'} size="sm" onClick={() => setTab('codes')} data-testid="tab-codes">
            <KeyRound className="h-4 w-4 mr-1" /> 発行コード一覧
          </Button>
          <Button variant={tab === 'settings' ? 'default' : 'outline'} size="sm" onClick={() => setTab('settings')} data-testid="tab-settings">
            <Settings className="h-4 w-4 mr-1" /> {t('admin_ref_tab_settings')}
          </Button>
        </div>

        {/* Referral List Tab */}
        {tab === 'list' && (
          <div>
            {/* Filter pills */}
            <div className="flex gap-2 mb-4" data-testid="referral-filters">
              {[
                { key: 'all', label: `すべて (${referrals.length})` },
                { key: 'completed', label: `完了 (${referrals.filter(r => r.status === 'completed').length})` },
                { key: 'pending', label: `保留中 (${referrals.filter(r => r.status === 'registered' || r.status === 'pending').length})` },
              ].map(f => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={filter === f.key ? 'default' : 'outline'}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>

            <div className="bg-card border rounded-2xl overflow-hidden" data-testid="ref-admin-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-secondary/30">
                    <th className="text-left p-3">{t('admin_ref_referrer')}</th>
                    <th className="text-left p-3">{t('admin_ref_referee')}</th>
                    <th className="text-left p-3">{t('admin_ref_status')}</th>
                    <th className="text-left p-3">{t('admin_ref_date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferrals.length === 0 ? (
                    <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">{t('admin_ref_empty')}</td></tr>
                  ) : filteredReferrals.map((ref) => (
                    <tr key={ref.id} className="border-b last:border-b-0 hover:bg-secondary/20">
                      <td className="p-3">
                        <div className="font-medium">{ref.referrer_name}</div>
                        <div className="text-xs text-muted-foreground">{ref.referrer_email}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{ref.referee_name}</div>
                        <div className="text-xs text-muted-foreground">{ref.referee_email}</div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ref.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'
                        }`}>{ref.status === 'completed' ? t('status_completed') : t('status_pending')}</span>
                      </td>
                      <td className="p-3 text-muted-foreground text-xs">
                        {ref.created_at ? new Date(ref.created_at).toLocaleDateString('ja-JP') : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Codes Tab */}
        {tab === 'codes' && (
          <div className="bg-card border rounded-2xl overflow-hidden" data-testid="ref-codes-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="text-left p-3">{t('admin_ref_code_col')}</th>
                  <th className="text-left p-3">{t('admin_ref_user_col')}</th>
                  <th className="text-center p-3">{t('admin_ref_count_col')}</th>
                  <th className="text-center p-3">{t('admin_ref_completed_col')}</th>
                  <th className="text-left p-3">{t('admin_ref_issued_col')}</th>
                  <th className="text-left p-3">{t('admin_ref_actions_col')}</th>
                </tr>
              </thead>
              <tbody>
                {codes.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{t('admin_ref_no_codes')}</td></tr>
                ) : codes.map((code) => (
                  <tr key={code.id} className="border-b last:border-b-0 hover:bg-secondary/20" data-testid={`code-row-${code.code}`}>
                    <td className="p-3">
                      {editingId === code.id ? (
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value.toUpperCase())}
                          className="h-8 w-40 font-mono text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(code.id);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          data-testid={`edit-code-input-${code.id}`}
                        />
                      ) : (
                        <span className="font-mono font-bold text-accent tracking-wider">{code.code}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{code.user_name}</div>
                      <div className="text-xs text-muted-foreground">{code.user_email}</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className="font-mono font-bold">{code.referral_count}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`font-mono font-bold ${code.completed_count > 0 ? 'text-emerald-600' : ''}`}>{code.completed_count}</span>
                    </td>
                    <td className="p-3 text-muted-foreground text-xs">
                      {code.created_at ? new Date(code.created_at).toLocaleDateString('ja-JP') : ''}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {editingId === code.id ? (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50" onClick={() => saveEdit(code.id)} data-testid={`save-code-${code.id}`}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={cancelEdit} data-testid={`cancel-edit-${code.id}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(code)} title="編集" data-testid={`edit-code-${code.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteCode(code.id, code.code)} title={t('admin_users_delete_btn')} data-testid={`delete-code-${code.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Settings Tab */}
        {tab === 'settings' && settings && (
          <div className="bg-card border rounded-2xl p-6 space-y-6" data-testid="ref-admin-settings">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>{t('admin_ref_referrer_reward')}</Label>
                <Input type="number" value={settings.referrer_free_transfers} onChange={(e) => setSettings({ ...settings, referrer_free_transfers: Number(e.target.value) })} />
                <p className="text-xs text-muted-foreground mt-1">{t('admin_ref_free_desc')}</p>
              </div>
              <div>
                <Label>{t('admin_ref_referee_reward')}</Label>
                <Input type="number" value={settings.referee_free_transfers} onChange={(e) => setSettings({ ...settings, referee_free_transfers: Number(e.target.value) })} />
                <p className="text-xs text-muted-foreground mt-1">{t('admin_ref_free_desc')}</p>
              </div>
              <div>
                <Label>{t('admin_ref_min_amount')}</Label>
                <Input type="number" value={settings.min_transfer_amount} onChange={(e) => setSettings({ ...settings, min_transfer_amount: Number(e.target.value) })} />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input type="checkbox" checked={settings.tier_enabled} onChange={(e) => setSettings({ ...settings, tier_enabled: e.target.checked })} className="h-4 w-4" data-testid="tier-toggle" />
              <Label>{t('admin_ref_tier_enabled')}</Label>
            </div>

            {settings.tier_enabled && (
              <div>
                <Label className="mb-3 block">{t('admin_ref_tier_config')}</Label>
                <div className="space-y-3">
                  {(settings.tiers || []).map((tier, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-3 items-end p-3 bg-secondary/30 rounded-lg">
                      <div>
                        <Label className="text-xs">{t('admin_ref_tier_name')}</Label>
                        <Input value={tier.label} onChange={(e) => updateTier(idx, 'label', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin_ref_tier_min')}</Label>
                        <Input type="number" value={tier.min_referrals} onChange={(e) => updateTier(idx, 'min_referrals', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">{t('admin_ref_tier_discount')}</Label>
                        <Input type="number" value={tier.fee_discount_percent} onChange={(e) => updateTier(idx, 'fee_discount_percent', e.target.value)} />
                      </div>
                      <div className="text-sm text-muted-foreground">{tier.fee_discount_percent}% OFF</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={handleSaveSettings} disabled={saving} className="gap-2" data-testid="save-ref-settings">
              <Save className="h-4 w-4" /> {saving ? t('saving') : t('save')}
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
